import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';

// Message templates for different occasions
const MESSAGE_TEMPLATES = {
  birthday: [
    "Wishing you a day filled with love, laughter, and unforgettable moments. Happy Birthday, {recipient}!",
    "May your special day be as wonderful as you are. Enjoy every moment!",
    "Another year older, another year of amazing memories. Celebrate big, {recipient}!",
    "Here's to a year filled with new adventures and cherished memories. Happy Birthday!",
    "May this year bring you closer to your dreams. Have a fantastic birthday, {recipient}!",
    "Your birthday is the perfect time to celebrate the amazing person you are!",
    "Wishing you joy, laughter, and wonderful surprises on your birthday!",
    "May your birthday be filled with precious moments you'll treasure forever.",
  ],
  // Specific Holiday Templates
  christmas: [
    "Wishing you a magical Christmas filled with joy, love, and cherished memories. Merry Christmas, {recipient}!",
    "May your Christmas be merry and bright, and your New Year filled with hope and happiness!",
    "Sending you warm wishes for a wonderful Christmas season and a joyful New Year!",
    "May the spirit of Christmas fill your heart with peace, love, and happiness.",
    "Wishing you all the blessings of a beautiful Christmas season!",
  ],
  hanukkah: [
    "Wishing you a bright and joyous Hanukkah filled with love and light. Happy Hanukkah, {recipient}!",
    "May the lights of Hanukkah illuminate your life with happiness and peace.",
    "Sending warm wishes for a wonderful Festival of Lights!",
    "May each candle you light bring blessings and joy to your home.",
    "Happy Hanukkah! May your celebration be filled with warmth and love.",
  ],
  kwanzaa: [
    "Wishing you a joyful Kwanzaa celebration filled with unity, purpose, and pride. Happy Kwanzaa, {recipient}!",
    "May the principles of Kwanzaa guide you throughout the year ahead.",
    "Celebrating heritage, culture, and community with you. Happy Kwanzaa!",
    "Wishing you a meaningful Kwanzaa filled with love and togetherness.",
  ],
  diwali: [
    "Wishing you a Diwali filled with light, love, and prosperity. Happy Diwali, {recipient}!",
    "May the festival of lights brighten your life and bring you joy!",
    "Sending warm wishes for a sparkling Diwali celebration!",
    "May this Diwali bring happiness, health, and prosperity to you and your family.",
    "Wishing you a beautiful Festival of Lights filled with joy and blessings!",
  ],
  eid: [
    "Wishing you a blessed Eid filled with joy, peace, and happiness. Eid Mubarak, {recipient}!",
    "May this special day bring you endless blessings and cherished memories.",
    "Eid Mubarak! Wishing you and your family a wonderful celebration!",
    "May Allah bless you with happiness and grace your home with warmth and peace.",
  ],
  lunar_new_year: [
    "Wishing you prosperity, good fortune, and happiness in the New Year. Happy Lunar New Year, {recipient}!",
    "May the Year bring you success, health, and abundant joy!",
    "Sending warm wishes for a prosperous and joyful Lunar New Year!",
    "May this new year be filled with good fortune and wonderful opportunities!",
  ],
  happy_holidays: [
    "Wishing you a joyful holiday season filled with warmth and happiness. Happy Holidays, {recipient}!",
    "May your holidays sparkle with moments of love, laughter, and goodwill.",
    "Sending warm wishes for a wonderful holiday season and a happy New Year!",
    "May the magic of the season bring you joy that lasts all year long!",
  ],
  // Generic holiday fallback
  holiday: [
    "May this holiday season fill your heart with warmth and joy. Happy Holidays, {recipient}!",
    "Wishing you peace, love, and happiness this holiday season.",
    "May your holidays sparkle with moments of love, laughter, and goodwill.",
    "May the magic of the holidays bring you joy that lasts all year long.",
    "Here's to a season filled with cozy moments and cherished memories!",
    "Wishing you a holiday season wrapped in happiness and tied with love.",
    "May your holidays be filled with the warmth of family and friends.",
  ],
  anniversary: [
    "Celebrating the beautiful journey you've shared together. Happy Anniversary, {recipient}!",
    "Here's to another year of love, laughter, and unforgettable memories!",
    "May your love continue to grow stronger with each passing year.",
    "Your love story inspires us all. Happy Anniversary!",
    "May your anniversary be filled with sweet memories and dreams of tomorrow.",
    "Celebrating the bond you share and the memories you've created together.",
  ],
  celebration: [
    "Here's to celebrating life's special moments with you, {recipient}!",
    "May this celebration be filled with joy, laughter, and wonderful memories!",
    "Wishing you a day as special as you are. Enjoy every moment!",
    "Celebrating you and all the joy you bring to our lives!",
    "Here's to making beautiful memories together!",
    "May this special day bring you happiness and smiles!",
  ],
  'just-because': [
    "Thinking of you and sending warm wishes your way, {recipient}!",
    "Just wanted to brighten your day and let you know you're appreciated!",
    "Here's a little something to remind you how special you are!",
    "Sending you love and positive vibes today and always!",
    "You deserve something special, just because you're you!",
    "Here's to celebrating YOU and all the joy you bring to others!",
  ],
  thankyou: [
    "Thank you for all that you do, {recipient}! Your kindness means the world to us.",
    "Your thoughtfulness and generosity have touched our hearts. Thank you!",
    "We're so grateful for you! Thank you for making a difference.",
    "A heartfelt thank you for being such an amazing person!",
    "Your support and kindness have meant so much. Thank you, {recipient}!",
    "Words cannot express how grateful we are for all you've done!",
  ],
  mothers_day: [
    "Happy Mother's Day, {recipient}! Thank you for all the love and memories.",
    "To the world's best mom - you deserve all the happiness today and always!",
    "Celebrating you today and every day. Happy Mother's Day!",
    "Your love, strength, and wisdom inspire us every day. Happy Mother's Day!",
    "Thank you for the endless love and beautiful memories. Happy Mother's Day, {recipient}!",
    "You make every day brighter. Wishing you a wonderful Mother's Day!",
  ],
  mothersday: [
    "Happy Mother's Day, {recipient}! Thank you for all the love and memories.",
    "To the world's best mom - you deserve all the happiness today and always!",
    "Celebrating you today and every day. Happy Mother's Day!",
    "Your love, strength, and wisdom inspire us every day. Happy Mother's Day!",
    "Thank you for the endless love and beautiful memories. Happy Mother's Day, {recipient}!",
    "You make every day brighter. Wishing you a wonderful Mother's Day!",
  ],
  fathers_day: [
    "Happy Father's Day, {recipient}! Thank you for all the guidance and love.",
    "To the world's best dad - you deserve all the happiness today!",
    "Celebrating you today and every day. Happy Father's Day!",
    "Your strength, wisdom, and love inspire us. Happy Father's Day!",
    "Thank you for the countless memories and unwavering support. Happy Father's Day, {recipient}!",
    "You're our hero every single day. Happy Father's Day!",
  ],
  fathersday: [
    "Happy Father's Day, {recipient}! Thank you for all the guidance and love.",
    "To the world's best dad - you deserve all the happiness today!",
    "Celebrating you today and every day. Happy Father's Day!",
    "Your strength, wisdom, and love inspire us. Happy Father's Day!",
    "Thank you for the countless memories and unwavering support. Happy Father's Day, {recipient}!",
    "You're our hero every single day. Happy Father's Day!",
  ],
  wedding: [
    "Congratulations on your wedding day, {recipient}! Wishing you a lifetime of love and happiness!",
    "May your marriage be filled with endless love, laughter, and beautiful memories!",
    "Here's to a lifetime of love, joy, and adventure together!",
    "Wishing you both all the happiness in the world. Congratulations!",
    "May your love story be as magical as your wedding day!",
    "Cheers to your beautiful beginning together. Congratulations!",
  ],
  engagement: [
    "Congratulations on your engagement, {recipient}! So excited for your journey ahead!",
    "What wonderful news! Wishing you both endless love and happiness!",
    "May your engagement be the start of a beautiful forever!",
    "So happy to celebrate this special milestone with you!",
    "Congratulations on finding your forever person!",
    "Wishing you a lifetime of love and joy together!",
  ],
  baby_shower: [
    "Congratulations on your baby shower, {recipient}! Can't wait to meet the little one!",
    "Wishing you all the best as you prepare for this beautiful new chapter!",
    "So excited to celebrate the upcoming arrival of your precious baby!",
    "May your days be filled with love, laughter, and tiny miracles!",
    "Congratulations on this wonderful blessing! So happy for you!",
    "Here's to sweet moments and beautiful memories with your little one!",
  ],
  new_baby: [
    "Congratulations on your new baby, {recipient}! Wishing you endless joy!",
    "Welcome to the world, little one! So happy for your growing family!",
    "May your days be filled with cuddles, giggles, and precious moments!",
    "Congratulations on this beautiful blessing! Enjoy every moment!",
    "Your family is now complete! Wishing you all the love and happiness!",
    "Welcome to parenthood! May it be filled with wonderful memories!",
  ],
  graduation: [
    "Congratulations on your graduation, {recipient}! The world is yours to conquer!",
    "So proud of all you've accomplished! Here's to your bright future!",
    "Your hard work has paid off. Congratulations, graduate!",
    "May this achievement be just the beginning of your success!",
    "Wishing you all the best as you embark on this exciting new chapter!",
    "Congratulations! Your future is filled with endless possibilities!",
  ],
  retirement: [
    "Happy Retirement, {recipient}! You've earned every moment of relaxation ahead!",
    "Congratulations on your retirement! Here's to new adventures!",
    "Wishing you a retirement filled with joy, relaxation, and cherished moments!",
    "You've worked hard for this moment. Enjoy your well-deserved retirement!",
    "May your retirement be as rewarding as your career has been!",
    "Here's to the next exciting chapter of your life!",
  ],
  housewarming: [
    "Congratulations on your new home, {recipient}! May it be filled with love and laughter!",
    "Wishing you happiness and wonderful memories in your new home!",
    "May your new house become a warm and welcoming home!",
    "Here's to new beginnings and beautiful memories in your new place!",
    "Congratulations! May your home be blessed with love and joy!",
    "Wishing you all the best as you settle into your new home!",
  ],
  memorial: [
    "In loving memory. May these cherished moments bring you comfort.",
    "Remembering with love and honoring precious memories.",
    "May these treasured memories bring peace and comfort to your heart.",
    "In remembrance of a life well-lived and deeply loved.",
    "Celebrating a beautiful life and the memories that will live forever.",
    "With heartfelt sympathy and fond remembrance.",
  ],
  pet: [
    "Celebrating the joy and love your furry friend brings every day!",
    "To the best pet ever! Thanks for all the unconditional love and happy memories!",
    "Here's to celebrating your amazing companion and the joy they bring!",
    "Your pet makes every day brighter! Celebrating all the paw-some moments!",
    "Wishing you and your furry friend many more happy years together!",
    "Celebrating the special bond you share with your beloved pet!",
  ],
  farewell: [
    "Wishing you all the best on your new journey, {recipient}!",
    "Though we'll miss you, we're excited for your next adventure!",
    "May your new path be filled with success and happiness!",
    "Farewell and best wishes for all that lies ahead!",
    "Thank you for the memories. Wishing you nothing but the best!",
    "As you move forward, know that you'll be missed but never forgotten!",
  ],
  custom: [
    "Sending you warm wishes and celebrating this special moment with you!",
    "Here's to making beautiful memories together, {recipient}!",
    "Wishing you joy, happiness, and wonderful moments ahead!",
    "Celebrating you and all the special moments we've shared!",
    "May this gift bring you as much joy as you bring to others!",
    "Here's to creating lasting memories and cherishing every moment!",
  ]
};

const MailInServiceEGiftPreview = ({ 
  isOpen, 
  onClose, 
  packageDetails,
  occasion,
  user,
  userEmail,
  userName
}) => {
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [fromName, setFromName] = useState(userName || '');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [usedMessageIndices, setUsedMessageIndices] = useState({});

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Safety check for packageDetails
  if (!packageDetails || typeof packageDetails !== 'object') {
    console.error('Invalid packageDetails:', packageDetails);
    return null;
  }

  // Occasion templates to get proper colors and display
  const occasionTemplates = {
    birthday: { name: 'Birthday', color: 'from-pink-500 to-purple-500', title: 'Happy Birthday!' },
    holiday: { name: 'Holiday', color: 'from-red-500 to-green-500', title: 'Happy Holidays!' },
    // Specific Holidays
    christmas: { name: 'Christmas', color: 'from-red-500 to-green-500', title: 'Merry Christmas!' },
    hanukkah: { name: 'Hanukkah', color: 'from-blue-500 to-blue-600', title: 'Happy Hanukkah!' },
    kwanzaa: { name: 'Kwanzaa', color: 'from-red-600 to-green-600', title: 'Happy Kwanzaa!' },
    diwali: { name: 'Diwali', color: 'from-yellow-500 to-orange-500', title: 'Happy Diwali!' },
    eid: { name: 'Eid', color: 'from-green-600 to-emerald-600', title: 'Eid Mubarak!' },
    lunar_new_year: { name: 'Lunar New Year', color: 'from-red-600 to-yellow-500', title: 'Happy Lunar New Year!' },
    happy_holidays: { name: 'Happy Holidays', color: 'from-red-500 to-green-500', title: 'Happy Holidays!' },
    // Other Occasions
    anniversary: { name: 'Anniversary', color: 'from-red-500 to-pink-600', title: 'Happy Anniversary!' },
    celebration: { name: 'Celebration', color: 'from-yellow-500 to-orange-600', title: 'Celebration!' },
    thankyou: { name: 'Thank You', color: 'from-blue-500 to-teal-600', title: 'Thank You!' },
    mothersday: { name: "Mother's Day", color: 'from-pink-400 to-rose-500', title: "Happy Mother's Day!" },
    fathersday: { name: "Father's Day", color: 'from-blue-500 to-indigo-600', title: "Happy Father's Day!" },
    wedding: { name: 'Wedding', color: 'from-rose-400 to-pink-500', title: 'Congratulations!' },
    engagement: { name: 'Engagement', color: 'from-purple-400 to-pink-500', title: 'Congratulations on Your Engagement!' },
    baby_shower: { name: 'Baby Shower', color: 'from-cyan-400 to-blue-400', title: 'Congratulations on Your Baby Shower!' },
    new_baby: { name: 'New Baby', color: 'from-yellow-400 to-amber-400', title: 'Welcome Little One!' },
    graduation: { name: 'Graduation', color: 'from-emerald-500 to-teal-600', title: 'Congratulations Graduate!' },
    retirement: { name: 'Retirement', color: 'from-amber-500 to-orange-600', title: 'Happy Retirement!' },
    housewarming: { name: 'Housewarming', color: 'from-green-500 to-emerald-600', title: 'Happy Housewarming!' },
    memorial: { name: 'In Remembrance', color: 'from-slate-400 to-gray-500', title: 'In Loving Memory' },
    pet: { name: 'Pet Celebration', color: 'from-orange-400 to-red-500', title: 'Pet Celebration!' },
    farewell: { name: 'Farewell', color: 'from-indigo-500 to-purple-600', title: 'Farewell!' },
    custom: { name: 'Custom', color: 'from-purple-500 to-blue-600', title: 'Special Gift!' }
  };

  const currentTemplate = occasionTemplates[occasion] || occasionTemplates.custom;



  // Generate suggested message based on occasion
  const generateSuggestedMessage = () => {
    const templates = MESSAGE_TEMPLATES[occasion] || MESSAGE_TEMPLATES['just-because'];
    
    // Get or initialize used indices for this occasion
    const used = usedMessageIndices[occasion] || [];
    
    // If all messages have been used, reset
    let availableIndices = [...Array(templates.length).keys()].filter(i => !used.includes(i));
    if (availableIndices.length === 0) {
      availableIndices = [...Array(templates.length).keys()];
      setUsedMessageIndices({ ...usedMessageIndices, [occasion]: [] });
    }
    
    // Pick a random message from available ones
    const randomIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
    let message = templates[randomIndex];
    
    // Personalize with recipient name
    const name = recipientName || 'there';
    message = message.replace(/{recipient}/g, name);
    
    // Update used indices
    setUsedMessageIndices({
      ...usedMessageIndices,
      [occasion]: [...used, randomIndex]
    });
    
    // Update message
    setPersonalMessage(message);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!recipientName || !recipientEmail || !fromName) {
      setError('Please fill in all required fields');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const payload = {
        package_id: String(packageDetails.id || ''),
        package_name: String(packageDetails.name || ''),
        photo_count: Number(packageDetails.photo_count || 0),
        base_price: Number(packageDetails.base_price || 0),
        addon_credits: Number(packageDetails.addon_credits || 0),
        addon_price: Number(packageDetails.addon_price || 0),
        shipping_cost: Number(packageDetails.shipping_cost || 0),
        total_amount: Number(packageDetails.total || 0),
        occasion: String(occasion || 'custom'),
        from_name: String(fromName || ''),
        to_name: String(recipientName || ''),
        to_email: String(recipientEmail || ''),
        personal_message: personalMessage || '',
        design_template: String(occasion || 'custom')
      };
      
      console.log('Sending mail-in gift checkout request:', payload);
      
      // Get user_id from user object
      const userId = user?.id;
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/api/mailin-gift/create-checkout?user_id=${userId}`,
        payload
      );

      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        setError('Failed to create checkout session');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Error creating gift checkout:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      
      // Handle different error response formats
      let errorMessage = 'Failed to process gift. Please try again.';
      
      if (err.response?.data) {
        const data = err.response.data;
        console.log('Full error data:', JSON.stringify(data, null, 2));
        
        // If detail is an array of validation errors
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map(e => `${e.loc?.[e.loc.length - 1] || 'field'}: ${e.msg || e.message || JSON.stringify(e)}`).join('; ');
        } 
        // If detail is a string
        else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        // If there's a message field
        else if (data.message) {
          errorMessage = data.message;
        }
        // If detail is an object
        else if (data.detail && typeof data.detail === 'object') {
          errorMessage = JSON.stringify(data.detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-4xl my-4 sm:my-8 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4 sm:p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <h2 className="text-lg sm:text-2xl font-bold truncate">Create The Perfect Gift</h2>
              <p className="text-xs sm:text-sm text-blue-100 mt-1 truncate">
                Send mail-in service as a gift
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-1 sm:gap-2 mt-4 sm:mt-6">
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white text-blue-600 flex items-center justify-center text-xs sm:text-sm font-bold">
                1
              </div>
              <span className="text-xs sm:text-sm font-medium hidden sm:inline">Design</span>
            </div>
            <div className="flex-1 h-1 bg-white rounded mx-1"></div>
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white text-blue-600 flex items-center justify-center text-xs sm:text-sm font-bold">
                2
              </div>
              <span className="text-xs sm:text-sm font-medium hidden sm:inline">Review & Sign</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-4">Review & Sign Your Gift</h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">Preview exactly what the recipient will see in their email</p>
          
          {/* Preview Card */}
          <div className={`bg-gradient-to-br ${currentTemplate.color} rounded-xl sm:rounded-2xl p-4 sm:p-8 mb-4 sm:mb-6 text-white relative overflow-hidden`}>
            {/* Theme-Specific Animated Background Elements */}
            <div className="absolute inset-0 opacity-30 pointer-events-none">
              {/* Birthday Animation - Balloons & Confetti */}
              {occasion === 'birthday' && (
                <>
                  {[...Array(15)].map((_, i) => (
                    <div
                      key={`confetti-${i}`}
                      className="absolute w-2 h-2 bg-white rounded-full"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: '-10px',
                        animation: `fall ${2 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    />
                  ))}
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={`balloon-${i}`}
                      className="absolute text-4xl"
                      style={{
                        left: `${10 + i * 15}%`,
                        bottom: '-50px',
                        animation: `rise ${4 + Math.random() * 2}s ease-in-out ${i * 0.5}s infinite`
                      }}
                    >
                      🎈
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes fall {
                      0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                      100% { transform: translateY(200vh) rotate(360deg); opacity: 0; }
                    }
                    @keyframes rise {
                      0% { transform: translateY(0); opacity: 0.5; }
                      100% { transform: translateY(-180vh); opacity: 0; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Anniversary Animation - Floating Hearts */}
              {occasion === 'anniversary' && (
                <>
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={`heart-${i}`}
                      className="absolute text-3xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        bottom: '-40px',
                        animation: `floatHeart ${4 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      ❤️
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes floatHeart {
                      0% { transform: translateY(0) scale(0.8); opacity: 0; }
                      10% { opacity: 1; }
                      90% { opacity: 1; }
                      100% { transform: translateY(-180px) scale(1.2); opacity: 0; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Celebration Animation - Sparkling Stars */}
              {occasion === 'celebration' && (
                <>
                  {[...Array(18)].map((_, i) => (
                    <div
                      key={`star-${i}`}
                      className="absolute text-yellow-200 text-2xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `sparkle ${1 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      ✨
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes sparkle {
                      0%, 100% { transform: scale(0.5) rotate(0deg); opacity: 0.3; }
                      50% { transform: scale(1.5) rotate(180deg); opacity: 1; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Christmas Animation - Trees & Snowflakes */}
              {occasion === 'christmas' && (
                <>
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={`snow-${i}`}
                      className="absolute text-white text-2xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: '-20px',
                        animation: `fallSnow ${5 + Math.random() * 5}s linear ${Math.random() * 3}s infinite`
                      }}
                    >
                      ❄️
                    </div>
                  ))}
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={`tree-${i}`}
                      className="absolute text-4xl"
                      style={{
                        left: `${10 + i * 12}%`,
                        bottom: '-40px',
                        animation: `rise ${4 + Math.random() * 2}s ease-in-out ${i * 0.5}s infinite`
                      }}
                    >
                      🎄
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes fallSnow {
                      0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                      100% { transform: translateY(200vh) rotate(360deg); opacity: 0.3; }
                    }
                    @keyframes rise {
                      0% { transform: translateY(0); opacity: 0.5; }
                      100% { transform: translateY(-180vh); opacity: 0; }
                    }
                  `}</style>
                </>
              )}

              {/* Hanukkah Animation - Menorahs & Stars of David */}
              {occasion === 'hanukkah' && (
                <>
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={`menorah-${i}`}
                      className="absolute text-3xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        bottom: '-40px',
                        animation: `floatUp ${4 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      🕎
                    </div>
                  ))}
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={`star-${i}`}
                      className="absolute text-2xl text-blue-200"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `twinkleStar ${2 + Math.random()}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      ✡️
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes floatUp {
                      0% { transform: translateY(0) scale(0.8); opacity: 0; }
                      10% { opacity: 1; }
                      90% { opacity: 1; }
                      100% { transform: translateY(-180px) scale(1.2); opacity: 0; }
                    }
                    @keyframes twinkleStar {
                      0%, 100% { transform: scale(0.5) rotate(0deg); opacity: 0.3; }
                      50% { transform: scale(1.3) rotate(180deg); opacity: 1; }
                    }
                  `}</style>
                </>
              )}

              {/* Kwanzaa Animation - Candles & Colors */}
              {occasion === 'kwanzaa' && (
                <>
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={`candle-${i}`}
                      className="absolute text-3xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        bottom: '-40px',
                        animation: `floatCandle ${4 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      🕯️
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes floatCandle {
                      0% { transform: translateY(0) scale(0.8); opacity: 0; }
                      10% { opacity: 1; }
                      90% { opacity: 1; }
                      100% { transform: translateY(-180px) scale(1.2); opacity: 0; }
                    }
                  `}</style>
                </>
              )}

              {/* Diwali Animation - Diyas & Sparkles */}
              {occasion === 'diwali' && (
                <>
                  {[...Array(15)].map((_, i) => (
                    <div
                      key={`diya-${i}`}
                      className="absolute text-3xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        bottom: '-40px',
                        animation: `floatDiya ${4 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      🪔
                    </div>
                  ))}
                  {[...Array(18)].map((_, i) => (
                    <div
                      key={`sparkle-${i}`}
                      className="absolute text-yellow-200 text-2xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `sparkle ${1 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      ✨
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes floatDiya {
                      0% { transform: translateY(0) scale(0.8); opacity: 0; }
                      10% { opacity: 1; }
                      90% { opacity: 1; }
                      100% { transform: translateY(-180px) scale(1.2); opacity: 0; }
                    }
                    @keyframes sparkle {
                      0%, 100% { transform: scale(0.5) rotate(0deg); opacity: 0.3; }
                      50% { transform: scale(1.5) rotate(180deg); opacity: 1; }
                    }
                  `}</style>
                </>
              )}

              {/* Eid Animation - Moons & Stars */}
              {occasion === 'eid' && (
                <>
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={`moon-${i}`}
                      className="absolute text-3xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        bottom: '-40px',
                        animation: `floatMoon ${4 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      🌙
                    </div>
                  ))}
                  {[...Array(15)].map((_, i) => (
                    <div
                      key={`star-${i}`}
                      className="absolute text-2xl text-yellow-200"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `twinkleStar ${2 + Math.random()}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      ⭐
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes floatMoon {
                      0% { transform: translateY(0) scale(0.8); opacity: 0; }
                      10% { opacity: 1; }
                      90% { opacity: 1; }
                      100% { transform: translateY(-180px) scale(1.2); opacity: 0; }
                    }
                    @keyframes twinkleStar {
                      0%, 100% { transform: scale(0.5); opacity: 0.3; }
                      50% { transform: scale(1.3); opacity: 1; }
                    }
                  `}</style>
                </>
              )}

              {/* Lunar New Year Animation - Red Envelopes & Lanterns */}
              {occasion === 'lunar_new_year' && (
                <>
                  {[...Array(15)].map((_, i) => (
                    <div
                      key={`envelope-${i}`}
                      className="absolute text-3xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: '-20px',
                        animation: `fallEnvelope ${4 + Math.random() * 3}s ease-in ${Math.random() * 3}s infinite`
                      }}
                    >
                      🧧
                    </div>
                  ))}
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={`lantern-${i}`}
                      className="absolute text-3xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `swayLantern ${3 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      🏮
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes fallEnvelope {
                      0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                      100% { transform: translateY(180vh) rotate(360deg); opacity: 0; }
                    }
                    @keyframes swayLantern {
                      0%, 100% { transform: translateX(-10px) rotate(-5deg); }
                      50% { transform: translateX(10px) rotate(5deg); }
                    }
                  `}</style>
                </>
              )}

              {/* Happy Holidays (Generic) Animation - Mixed Elements */}
              {occasion === 'happy_holidays' && (
                <>
                  {[...Array(15)].map((_, i) => (
                    <div
                      key={`sparkle-${i}`}
                      className="absolute text-yellow-200 text-2xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `sparkle ${1 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      ✨
                    </div>
                  ))}
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={`snow-${i}`}
                      className="absolute text-white text-2xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: '-20px',
                        animation: `fallSnow ${5 + Math.random() * 5}s linear ${Math.random() * 3}s infinite`
                      }}
                    >
                      ❄️
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes sparkle {
                      0%, 100% { transform: scale(0.5) rotate(0deg); opacity: 0.3; }
                      50% { transform: scale(1.5) rotate(180deg); opacity: 1; }
                    }
                    @keyframes fallSnow {
                      0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                      100% { transform: translateY(200vh) rotate(360deg); opacity: 0.3; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Thank You Animation - Gentle Hearts */}
              {occasion === 'thankyou' && (
                <>
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={`thankyou-${i}`}
                      className="absolute text-2xl"
                      style={{
                        left: `${20 + i * 8}%`,
                        top: `${20 + (i % 3) * 25}%`,
                        animation: `gentlePulse ${2 + Math.random()}s ease-in-out ${i * 0.3}s infinite`
                      }}
                    >
                      💙
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes gentlePulse {
                      0%, 100% { transform: scale(0.8); opacity: 0.4; }
                      50% { transform: scale(1.3); opacity: 0.9; }
                    }
                  `}</style>
                </>
              )}

              
              {/* Mother's Day Animation - Floating Flowers */}
              {occasion === 'mothersday' && (
                <>
                  {[...Array(15)].map((_, i) => (
                    <div
                      key={`flower-${i}`}
                      className="absolute text-3xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        bottom: '-50px',
                        animation: `floatFlower ${4 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      🌸
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes floatFlower {
                      0% { transform: translateY(0) rotate(0deg); opacity: 0; }
                      10% { opacity: 1; }
                      90% { opacity: 1; }
                      100% { transform: translateY(-200px) rotate(360deg); opacity: 0; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Father's Day Animation - Rising Stars */}
              {occasion === 'fathersday' && (
                <>
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={`star-${i}`}
                      className="absolute text-2xl text-yellow-300"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `twinkleStar ${2 + Math.random()}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      ⭐
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes twinkleStar {
                      0%, 100% { transform: scale(0.5); opacity: 0.3; }
                      50% { transform: scale(1.3); opacity: 1; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Wedding Animation - Floating Petals */}
              {occasion === 'wedding' && (
                <>
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={`petal-${i}`}
                      className="absolute text-2xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: '-20px',
                        animation: `fallPetal ${4 + Math.random() * 3}s ease-in ${Math.random() * 3}s infinite`
                      }}
                    >
                      🌹
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes fallPetal {
                      0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                      100% { transform: translateY(180vh) rotate(720deg); opacity: 0; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Engagement Animation - Rings & Hearts */}
              {occasion === 'engagement' && (
                <>
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={`ring-${i}`}
                      className="absolute text-4xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `floatRing ${4 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      💍
                    </div>
                  ))}
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={`heart-${i}`}
                      className="absolute text-3xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `floatHeart ${4 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      💕
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes floatRing {
                      0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.7; }
                      50% { transform: translateY(-40px) rotate(360deg); opacity: 1; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Baby Shower Animation - Baby Items */}
              {occasion === 'baby_shower' && (
                <>
                  {[...Array(15)].map((_, i) => {
                    const emojis = ['🍼', '👶', '🎈', '🧸', '🎀'];
                    const emoji = emojis[i % emojis.length];
                    return (
                      <div
                        key={`baby-${i}`}
                        className="absolute text-3xl"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animation: `floatBaby ${3 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                        }}
                      >
                        {emoji}
                      </div>
                    );
                  })}
                  <style jsx>{`
                    @keyframes floatBaby {
                      0%, 100% { transform: translateY(0) scale(1); opacity: 0.6; }
                      50% { transform: translateY(-30px) scale(1.1); opacity: 1; }
                    }
                  `}</style>
                </>
              )}
              
              {/* New Baby Animation - Stars & Baby */}
              {occasion === 'new_baby' && (
                <>
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={`newbaby-${i}`}
                      className="absolute text-4xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `gentleFloat ${4 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      👶
                    </div>
                  ))}
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={`star-${i}`}
                      className="absolute text-2xl text-yellow-300"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `twinkleStar ${2 + Math.random()}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      ⭐
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes gentleFloat {
                      0%, 100% { transform: translateY(0); opacity: 0.7; }
                      50% { transform: translateY(-25px); opacity: 1; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Graduation Animation - Caps & Diplomas */}
              {occasion === 'graduation' && (
                <>
                  {[...Array(12)].map((_, i) => {
                    const symbols = ['🎓', '📜', '🏆'];
                    const symbol = symbols[i % symbols.length];
                    return (
                      <div
                        key={`grad-${i}`}
                        className="absolute text-4xl"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animation: `tossGrad ${3 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                        }}
                      >
                        {symbol}
                      </div>
                    );
                  })}
                  <style jsx>{`
                    @keyframes tossGrad {
                      0% { transform: translateY(0) rotate(0deg); opacity: 0.5; }
                      50% { transform: translateY(-50px) rotate(180deg); opacity: 1; }
                      100% { transform: translateY(0) rotate(360deg); opacity: 0.5; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Retirement Animation - Relaxation Theme */}
              {occasion === 'retirement' && (
                <>
                  {[...Array(15)].map((_, i) => {
                    const emojis = ['🌴', '🏖️', '☀️', '🍹', '⛱️'];
                    const emoji = emojis[i % emojis.length];
                    return (
                      <div
                        key={`retire-${i}`}
                        className="absolute text-3xl"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animation: `relaxFloat ${4 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                        }}
                      >
                        {emoji}
                      </div>
                    );
                  })}
                  <style jsx>{`
                    @keyframes relaxFloat {
                      0%, 100% { transform: translateY(0) rotate(-5deg); opacity: 0.6; }
                      50% { transform: translateY(-20px) rotate(5deg); opacity: 1; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Housewarming Animation - Houses & Keys */}
              {occasion === 'housewarming' && (
                <>
                  {[...Array(12)].map((_, i) => {
                    const symbols = ['🏠', '🔑', '🏡', '🚪'];
                    const symbol = symbols[i % symbols.length];
                    return (
                      <div
                        key={`house-${i}`}
                        className="absolute text-3xl"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animation: `floatHome ${4 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                        }}
                      >
                        {symbol}
                      </div>
                    );
                  })}
                  <style jsx>{`
                    @keyframes floatHome {
                      0%, 100% { transform: translateY(0) scale(1); opacity: 0.7; }
                      50% { transform: translateY(-30px) scale(1.1); opacity: 1; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Memorial Animation - Gentle Doves & Flowers */}
              {occasion === 'memorial' && (
                <>
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={`dove-${i}`}
                      className="absolute text-3xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `gentleFloat ${5 + Math.random() * 3}s ease-in-out ${Math.random() * 3}s infinite`
                      }}
                    >
                      🕊️
                    </div>
                  ))}
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={`flower-${i}`}
                      className="absolute text-2xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `fadeIn ${4 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      🌸
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes fadeIn {
                      0%, 100% { opacity: 0.3; transform: scale(0.9); }
                      50% { opacity: 0.8; transform: scale(1); }
                    }
                  `}</style>
                </>
              )}
              
              {/* Pet Celebration Animation - Paw Prints & Hearts */}
              {occasion === 'pet' && (
                <>
                  {[...Array(15)].map((_, i) => {
                    const emojis = ['🐾', '🐶', '🐱', '❤️', '🦴'];
                    const emoji = emojis[i % emojis.length];
                    return (
                      <div
                        key={`pet-${i}`}
                        className="absolute text-3xl"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animation: `playfulBounce ${2 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                        }}
                      >
                        {emoji}
                      </div>
                    );
                  })}
                  <style jsx>{`
                    @keyframes playfulBounce {
                      0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.6; }
                      25% { transform: translateY(-20px) rotate(-10deg); opacity: 1; }
                      75% { transform: translateY(-20px) rotate(10deg); opacity: 1; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Farewell Animation - Waving & Balloons */}
              {occasion === 'farewell' && (
                <>
                  {[...Array(12)].map((_, i) => {
                    const emojis = ['👋', '🎈', '✈️', '🌟'];
                    const emoji = emojis[i % emojis.length];
                    return (
                      <div
                        key={`farewell-${i}`}
                        className="absolute text-3xl"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animation: `floatAway ${4 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`
                        }}
                      >
                        {emoji}
                      </div>
                    );
                  })}
                  <style jsx>{`
                    @keyframes floatAway {
                      0% { transform: translateY(0) translateX(0); opacity: 0.6; }
                      50% { transform: translateY(-40px) translateX(20px); opacity: 1; }
                      100% { transform: translateY(-80px) translateX(40px); opacity: 0.3; }
                    }
                  `}</style>
                </>
              )}
              
              {/* Holiday (Generic) Animation - Mixed Holiday Elements */}
              {occasion === 'holiday' && (
                <>
                  {[...Array(15)].map((_, i) => {
                    const emojis = ['🎉', '🎊', '✨', '🎁', '🌟'];
                    const emoji = emojis[i % emojis.length];
                    return (
                      <div
                        key={`holiday-${i}`}
                        className="absolute text-3xl"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animation: `celebrateHoliday ${3 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                        }}
                      >
                        {emoji}
                      </div>
                    );
                  })}
                  <style jsx>{`
                    @keyframes celebrateHoliday {
                      0%, 100% { transform: translateY(0) scale(1); opacity: 0.6; }
                      50% { transform: translateY(-35px) scale(1.2); opacity: 1; }
                    }
                  `}</style>
                </>
              )}

              {/* Custom/Just Because Animation - Gentle Sparkles */}
              {(occasion === 'custom' || occasion === 'just-because') && (
                <>
                  {[...Array(15)].map((_, i) => (
                    <div
                      key={`sparkle-${i}`}
                      className="absolute text-yellow-200 text-2xl"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animation: `gentleSparkle ${2 + Math.random() * 2}s ease-in-out ${Math.random() * 2}s infinite`
                      }}
                    >
                      ✨
                    </div>
                  ))}
                  <style jsx>{`
                    @keyframes gentleSparkle {
                      0%, 100% { transform: scale(0.6) rotate(0deg); opacity: 0.4; }
                      50% { transform: scale(1.2) rotate(180deg); opacity: 1; }
                    }
                  `}</style>
                </>
              )}

            </div>
            
            <div className="relative z-10 text-center">
              <div className="mb-3 sm:mb-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-white/30 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <svg className="w-10 h-10 sm:w-12 sm:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl sm:text-3xl font-bold mb-1 sm:mb-2">{currentTemplate.title}</h2>
              <div className="text-sm sm:text-lg mb-2">
                From: <span className="font-bold">{fromName || 'Your Name'}</span>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute top-4 left-4 text-white/20">
              <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div className="absolute bottom-4 right-4 text-white/20">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
          </div>

          {/* What Your Gift Unlocks Section */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <h3 className="text-base sm:text-xl font-bold text-gray-800">What Your Gift Unlocks</h3>
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 text-center">
              See how mail-in service transforms photos into magical animated moments
            </p>

            {/* 4-step animation cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* Step 1: Mail Your Photos */}
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-3 sm:p-4 rounded-lg sm:rounded-xl text-white">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-sm sm:text-lg mb-0.5 sm:mb-1">Mail Your Photos</h4>
                    <p className="text-xs sm:text-sm text-white/90">Pack your precious photos safely and mail them to us</p>
                  </div>
                </div>
              </div>

              {/* Step 2: We Digitize */}
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 sm:p-4 rounded-lg sm:rounded-xl text-white">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-sm sm:text-lg mb-0.5 sm:mb-1">We Digitize</h4>
                    <p className="text-xs sm:text-sm text-white/90">Professional scanning and digitization of each photo</p>
                  </div>
                </div>
              </div>

              {/* Step 3: AI Animation */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-3 sm:p-4 rounded-lg sm:rounded-xl text-white">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-sm sm:text-lg mb-0.5 sm:mb-1">AI Animation</h4>
                    <p className="text-xs sm:text-sm text-white/90">Bring your memories to life with magical animations</p>
                  </div>
                </div>
              </div>

              {/* Step 4: Keep Your Memories */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-3 sm:p-4 rounded-lg sm:rounded-xl text-white">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-sm sm:text-lg mb-0.5 sm:mb-1">Keep Your Memories</h4>
                    <p className="text-xs sm:text-sm text-white/90">We return your originals plus 3 years of free digital access</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Package Details */}
            <div className="bg-white rounded-xl p-4 mt-6 shadow-md">
              <div className="text-center mb-4">
                <h4 className="text-lg font-bold text-gray-800">{String(packageDetails.name || '')}</h4>
                <p className="text-sm text-gray-600">{String(packageDetails.photo_count || 0)} Photos Digitized</p>
              </div>
              
              {Number(packageDetails.addon_credits || 0) > 0 && (
                <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg p-3 mb-4">
                  <p className="text-center text-purple-800 font-semibold text-sm flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    +{String(packageDetails.addon_credits || 0)} Animation Credits
                  </p>
                </div>
              )}
              
              <div className="border-t border-gray-200 pt-3 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Digitization</span>
                  <span>${Number(packageDetails.base_price || 0).toFixed(2)}</span>
                </div>
                {Number(packageDetails.addon_credits || 0) > 0 && (
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Animation Credits</span>
                    <span>${Number(packageDetails.addon_price || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Shipping (Roundtrip)</span>
                  <span>${Number(packageDetails.shipping_cost || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-gray-800 pt-2 border-t border-gray-300">
                  <span>Total</span>
                  <span>${Number(packageDetails.total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Gift Details Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* From Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Name *
              </label>
              <input
                type="text"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                placeholder="Jamie (Founder)"
                required
              />
            </div>

            {/* Recipient Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Recipient's Name *
              </label>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                placeholder="Who is this gift for?"
                required
              />
            </div>

            {/* Recipient Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Recipient's Email *
              </label>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                placeholder="recipient@example.com"
                required
              />
              <p className="text-xs text-gray-500 mt-2 flex items-start gap-1.5">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  The gift redemption code and instructions will be sent to this email address upon purchase
                </span>
              </p>
            </div>

            {/* Personal Message */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Personal Message (Optional)
                </label>
                <button
                  type="button"
                  onClick={generateSuggestedMessage}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Suggest Message
                </button>
              </div>
              <textarea
                value={personalMessage}
                onChange={(e) => setPersonalMessage(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors resize-none"
                rows="3"
                placeholder="Add a personal touch to your gift..."
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-gray-500">
                  {personalMessage.length}/500 characters
                </p>
                <p className="text-xs text-purple-600 italic">
                  💡 Click "Suggest Message" for inspiration
                </p>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 p-4 sm:p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 font-medium text-sm sm:text-base transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={processing || !recipientName || !recipientEmail || !fromName}
            className={`px-6 py-2.5 rounded-lg font-bold text-sm sm:text-base transition-all ${
              processing || !recipientName || !recipientEmail || !fromName
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:opacity-90 shadow-lg'
            }`}
          >
            {processing ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              <>Proceed to Payment - ${Number(packageDetails?.total || 0).toFixed(2)}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default MailInServiceEGiftPreview;
